<template>
  <div>
    <loader :loading="loading"/>
    <div v-if="!loading">
      <loader v-if="loadingCategories"/>
      <page-title :title="$t('task.create.label')"/>

      <panel title="Ação">
        <div class="l-row">
          <form-group label="Módulo" :columns="6" :error="getError('module')">
            <select v-model="moduleSelected" class="form-select">
              <option :value=null>{{ $t('choose.label') }}</option>
              <option :value="`${module.type}.${module.identifier}`" v-for="module in toolBatchTaskModules">
                {{ $t(`${module.type}.label`) }} {{ $t(`${module.identifier}.label`) }}
              </option>
            </select>
          </form-group>

          <form-group label="Ação" :columns="6" v-if="moduleSelected && toolBatchTaskActions.length" :error="getError('action')">
            <select v-model="actionSelected" class="form-select">
              <option :value=null>{{ $t('choose.label') }}</option>
              <option :value="action.identifier" v-for="action in toolBatchTaskActions">
                {{ action.label }}
              </option>
            </select>
          </form-group>

          <form-group label="Fonte" :columns="6" v-if="sources.length && module.type === 'marketplace'" :error="getError('source')" :required="true">
            <select v-model="sourceSelected" class="form-select">
              <option :value=null>{{ $t('choose.label') }}</option>
              <option :value="`${source.type}.${source.identifier}`" v-for="source in sources">
                {{ $t(`${source.type}.label`) }} {{ $t(`${source.identifier}.label`) }}
              </option>
            </select>
          </form-group>
        </div>
      </panel>

      <panel title="Configurações">
        <form-group label="Observação" :columns="12">
          <textarea v-model="comment" class="form-textarea"></textarea>
        </form-group>
      </panel>

      <panel title="Opções da ação" v-if="actionSelected && action.hasOptions">
        <component :is="actionOptionComponent" ref="actionOptionComponent" :module="module"/>
      </panel>

      <panel :title="`Filtros do módulo (${$t(`${module.type}.label`)} ${$t(`${module.identifier}.label`)})`" v-if="actionSelected && action.hasModuleFilters">
        <component :is="actionModuleFilterComponent" ref="actionModuleFilterComponent" :module="module"/>
      </panel>

      <panel title="Filtros de produto (Agrupador e SKU)" v-if="showProductCodesFilter">
        <product-codes ref="productCodesFilterComponent"/>
      </panel>

      <panel :title="`Filtros da fonte (${$t(`${source.type}.label`)} ${$t(`${source.identifier}.label`)})`" v-if="source && action.hasOptions && action.hasSourceFilters">
        <component :is="actionSourceFilterComponent" ref="actionSourceFilterComponent" :source="source"/>
      </panel>

      <panel>
        <button :disabled="saving || canNotSave" v-bind:class="{ 'is-loading': saving }" class="btn btn--success" @click="showModal">{{ $t('task.create.label') }}</button>
      </panel>
      <modal-confirm v-model="showModalConfirm" v-on:option="save()"/>
    </div>
  </div>
</template>

<script>
  import api from '@/api'
  import notifier from '@/notifier'
  import { upperFirst, merge } from 'lodash'
  import { mapActions, mapGetters } from 'vuex'
  import { mapFields } from 'vuex-map-fields'
  import Loader from '@/components/template/Loader'
  import PageTitle from '@/components/template/PageTitle'
  import Panel from '@/components/template/Panel'
  import FormGroup from '@/components/template/FormGroup'
  import ModalConfirm from '@/components/Task/ModalConfirm'
  import ProductCodes from '@/components/tool/batch/shared/filters/ProductCodes'

  // import ExportProductCsvEcommerceMoovinSource from '@/components/tool/batch/exportProductCsv/filters/ecommerce/Moovin.vue'
  import ExportProductCsvErpTinySource from '@/components/tool/batch/exportProductCsv/filters/erp/Tiny.vue'
  import ExportProductCsvToolApiSource from '@/components/tool/batch/exportProductCsv/filters/tool/Api.vue'
  import ExportProductCsvMarketplaceCarrefourModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Carrefour.vue'
  import ExportProductCsvMarketplaceCnovaModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Cnova.vue'
  import ExportProductCsvMarketplaceColomboModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Colombo.vue'
  import ExportProductCsvMarketplaceGfgModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Gfg.vue'
  import ExportProductCsvMarketplaceMagaluModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Magalu.vue'
  import ExportProductCsvMarketplaceLeroyModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Leroy.vue'
  import ExportProductCsvMarketplaceAmazonModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Amazon.vue'
  import ExportProductCsvMarketplaceMadeiraModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Madeira.vue'
  import ExportProductCsvMarketplaceMercadoLivreModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/MercadoLivre.vue'
  import ExportProductCsvMarketplaceNetshoesModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Netshoes.vue'
  import ExportProductCsvMarketplaceSkyhubModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Skyhub.vue'
  import ExportProductCsvMarketplaceSicoobModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Sicoob.vue'
  import ExportProductCsvMarketplaceSicrediModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Sicredi.vue'
  import ExportProductCsvMarketplaceMaisCorreiosModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/MaisCorreios.vue'
  import ExportProductCsvMarketplaceWalmartModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Walmart.vue'
  import ExportProductCsvMarketplaceWebcontinentalModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Webcontinental.vue'
  import ExportProductCsvMarketplaceZoomModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Zoom.vue'
  import ExportProductCsvMarketplacePolishopModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Polishop.vue'
  import ExportProductCsvMarketplaceCentauroModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Centauro.vue'
  import ExportProductCsvToolApiModule from '@/components/tool/batch/exportProductCsv/filters/module/tool/Api.vue'
  import ExportProductCsvMarketplaceShopeeModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Shopee.vue'
  import ExportProductCsvMarketplaceSheinModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/Shein.vue'
  import ExportProductCsvMarketplaceTikTokShopModule from '@/components/tool/batch/exportProductCsv/filters/marketplace/TikTokShop.vue'

  import ExportOrderCsvMarketplaceSkyhubModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Skyhub.vue'
  import ExportOrderCsvMarketplaceCarrefourModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Carrefour.vue'
  import ExportOrderCsvMarketplaceCnovaModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Cnova.vue'
  import ExportOrderCsvMarketplaceColomboModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Colombo.vue'
  import ExportOrderCsvMarketplaceCentauroModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Centauro.vue'
  import ExportOrderCsvMarketplaceMagaluModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Magalu.vue'
  import ExportOrderCsvMarketplaceLeroyModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Leroy.vue'
  import ExportOrderCsvMarketplaceAmazonModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Amazon.vue'
  import ExportOrderCsvMarketplaceSicoobModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Sicoob.vue'
  import ExportOrderCsvMarketplaceSicrediModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Sicredi.vue'
  import ExportOrderCsvMarketplaceMaisCorreiosModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/MaisCorreios.vue'
  import ExportOrderCsvMarketplaceMadeiraModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Madeira.vue'
  import ExportOrderCsvMarketplaceMercadoLivreModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/MercadoLivre.vue'
  import ExportOrderCsvMarketplaceNetshoesModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Netshoes.vue'
  import ExportOrderCsvMarketplaceWebcontinentalModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Webcontinental.vue'
  import ExportOrderCsvMarketplaceZoomModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Zoom.vue'
  import ExportOrderCsvMarketplacePolishopModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Polishop.vue'
  import ExportOrderCsvMarketplaceShopeeModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Shopee.vue'
  import ExportOrderCsvMarketplaceSheinModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/Shein.vue'
  import ExportOrderCsvMarketplaceTikTokShopModule from '@/components/tool/batch/exportOrderCsv/filters/marketplace/TikTokShop.vue'

  import ReportOrderStatusMarketplaceSharedModule from '@/components/tool/batch/reportOrderStatus/filters/marketplace/Shared.vue'

  import FeedUpdatePriceOptions from '@/components/tool/batch/feedUpdatePrice/Options.vue'
  import FeedUpdateReducerOptions from '@/components/tool/batch/feedUpdateReducer/Options.vue'
  import FileUpdatePriceOptions from '@/components/tool/batch/fileUpdatePrice/Options.vue'
  import FileReprocessOrderOptions from '@/components/tool/batch/fileReprocessOrder/Options.vue'
  import UpdatePriceOptions from '@/components/tool/batch/updatePrice/Options.vue'
  import UpdateReducerOptions from '@/components/tool/batch/updateReducer/Options.vue'
  import ExportProductCsvOptions from '@/components/tool/batch/exportProductCsv/Options.vue'

  import ProductReprocessMarketplaceAmazonModule from '@/components/tool/batch/productReprocess/filters/marketplace/Shared.vue'

  import FeedUpdatePriceEcommerceMoovinSource from '@/components/tool/batch/feedUpdatePrice/filters/ecommerce/Moovin.vue'
  import FeedUpdatePriceErpTinySource from '@/components/tool/batch/feedUpdatePrice/filters/erp/Tiny.vue'
  import FeedUpdatePriceMarketplaceCarrefourModule from '@/components/tool/batch/feedUpdatePrice/filters/marketplace/Carrefour.vue'
  import FeedUpdatePriceMarketplaceGfgModule from '@/components/tool/batch/feedUpdatePrice/filters/marketplace/Gfg.vue'
  import FeedUpdatePriceMarketplaceLeroyModule from '@/components/tool/batch/feedUpdatePrice/filters/marketplace/Leroy.vue'
  import FeedUpdatePriceMarketplaceAmazonModule from '@/components/tool/batch/feedUpdatePrice/filters/marketplace/Amazon.vue'
  import FeedUpdatePriceToolApiSource from '@/components/tool/batch/feedUpdatePrice/filters/tool/Api.vue'

  import FeedUpdateReducerEcommerceMoovinSource from '@/components/tool/batch/feedUpdateReducer/filters/ecommerce/Moovin.vue'
  import FeedUpdateReducerErpTinySource from '@/components/tool/batch/feedUpdateReducer/filters/erp/Tiny.vue'
  import FeedUpdateReducerMarketplaceCarrefourModule from '@/components/tool/batch/feedUpdateReducer/filters/marketplace/Carrefour.vue'
  import FeedUpdateReducerMarketplaceGfgModule from '@/components/tool/batch/feedUpdateReducer/filters/marketplace/Gfg.vue'
  import FeedUpdateReducerMarketplaceLeroyModule from '@/components/tool/batch/feedUpdateReducer/filters/marketplace/Leroy.vue'
  import FeedUpdateReducerMarketplaceAmazonModule from '@/components/tool/batch/feedUpdateReducer/filters/marketplace/Amazon.vue'
  import FeedUpdateReducerToolApiSource from '@/components/tool/batch/feedUpdateReducer/filters/tool/Api.vue'

  import MigrationAnnouncementMarketplaceMercadoLivreModule from '@/components/tool/batch/migrationAnnouncement/filters/marketplace/MercadoLivre.vue'

  import ReprocessFeedPriceMarketplaceCarrefourModule from '@/components/tool/batch/reprocessFeedPrice/filters/marketplace/Carrefour.vue'
  import ReprocessFeedPriceMarketplaceGfgModule from '@/components/tool/batch/reprocessFeedPrice/filters/marketplace/Gfg.vue'
  import ReprocessFeedPriceMarketplaceLeroyModule from '@/components/tool/batch/reprocessFeedPrice/filters/marketplace/Leroy.vue'
  import ReprocessFeedPriceMarketplaceAmazonModule from '@/components/tool/batch/reprocessFeedPrice/filters/marketplace/Amazon.vue'
  import ReprocessFeedProductMarketplaceCarrefourModule from '@/components/tool/batch/reprocessFeedProduct/filters/marketplace/Carrefour.vue'
  import ReprocessFeedProductMarketplaceGfgModule from '@/components/tool/batch/reprocessFeedProduct/filters/marketplace/Gfg.vue'
  import ReprocessFeedProductMarketplaceLeroyModule from '@/components/tool/batch/reprocessFeedProduct/filters/marketplace/Leroy.vue'
  import ReprocessFeedProductMarketplaceAmazonModule from '@/components/tool/batch/reprocessFeedProduct/filters/marketplace/Amazon.vue'
  import ReprocessFeedStockMarketplaceCarrefourModule from '@/components/tool/batch/reprocessFeedStock/filters/marketplace/Carrefour.vue'
  import ReprocessFeedStockMarketplaceGfgModule from '@/components/tool/batch/reprocessFeedStock/filters/marketplace/Gfg.vue'
  import ReprocessFeedStockMarketplaceLeroyModule from '@/components/tool/batch/reprocessFeedStock/filters/marketplace/Leroy.vue'
  import ReprocessFeedStockMarketplaceAmazonModule from '@/components/tool/batch/reprocessFeedStock/filters/marketplace/Amazon.vue'
  import ReprocessPriceMarketplaceCnovaModule from '@/components/tool/batch/reprocessPrice/filters/marketplace/Cnova.vue'
  import ReprocessPriceMarketplaceColomboModule from '@/components/tool/batch/reprocessPrice/filters/marketplace/Colombo.vue'
  import ReprocessPriceMarketplaceMagaluModule from '@/components/tool/batch/reprocessPrice/filters/marketplace/Magalu.vue'
  import ReprocessPriceMarketplaceShopeeModule from '@/components/tool/batch/reprocessPrice/filters/marketplace/Shopee.vue'
  import ReprocessPriceMarketplaceMadeiraModule from '@/components/tool/batch/reprocessPrice/filters/marketplace/Madeira.vue'
  import ReprocessPriceMarketplaceNetshoesModule from '@/components/tool/batch/reprocessPrice/filters/marketplace/Netshoes.vue'
  import ReprocessPriceMarketplaceSkyhubModule from '@/components/tool/batch/reprocessPrice/filters/marketplace/Skyhub.vue'
  import ReprocessPriceMarketplaceWalmartModule from '@/components/tool/batch/reprocessPrice/filters/marketplace/Walmart.vue'
  import ReprocessPriceMarketplaceWebcontinentalModule from '@/components/tool/batch/reprocessPrice/filters/marketplace/Webcontinental.vue'
  import ReprocessPriceMarketplaceZoomModule from '@/components/tool/batch/reprocessPrice/filters/marketplace/Zoom.vue'
  import ReprocessPriceMarketplaceCentauroModule from '@/components/tool/batch/reprocessPrice/filters/marketplace/Centauro.vue'

  import ReprocessProductMarketplaceCnovaModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Cnova.vue'
  import ReprocessProductMarketplaceShopeeModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Shopee.vue'
  import ReprocessProductMarketplaceColomboModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Colombo.vue'
  import ReprocessProductMarketplaceMagaluModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Magalu.vue'
  import ReprocessProductMarketplaceMadeiraModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Madeira.vue'
  import ReprocessProductMarketplaceNetshoesModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Netshoes.vue'
  import ReprocessProductMarketplaceSkyhubModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Skyhub.vue'
  import ReprocessProductMarketplaceWalmartModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Walmart.vue'
  import ReprocessProductMarketplaceWebcontinentalModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Webcontinental.vue'
  import ReprocessProductMarketplaceZoomModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Zoom.vue'
  import ReprocessProductMarketplaceCentauroModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Centauro.vue'
  import ReprocessProductMarketplaceSicoobModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Sicoob.vue'
  import ReprocessProductMarketplaceSicrediModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Sicredi.vue'
  import ReprocessProductMarketplaceMaisCorreiosModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/MaisCorreios.vue'
  import ReprocessProductMarketplacePolishopModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Polishop.vue'
  import ReprocessProductMarketplaceRennerModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Renner.vue'
  import ReprocessProductMarketplacePrivaliaModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Privalia.vue'
  import ReprocessProductMarketplaceSheinModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/Shein.vue'
  import ReprocessProductMarketplaceTikTokShopModule from '@/components/tool/batch/reprocessProduct/filters/marketplace/TikTokShop.vue'

  import ReprocessStockMarketplaceCnovaModule from '@/components/tool/batch/reprocessStock/filters/marketplace/Cnova.vue'
  import ReprocessStockMarketplaceColomboModule from '@/components/tool/batch/reprocessStock/filters/marketplace/Colombo.vue'
  import ReprocessStockMarketplaceShopeeModule from '@/components/tool/batch/reprocessStock/filters/marketplace/Shopee.vue'
  import ReprocessStockMarketplaceMagaluModule from '@/components/tool/batch/reprocessStock/filters/marketplace/Magalu.vue'
  import ReprocessStockMarketplaceMadeiraModule from '@/components/tool/batch/reprocessStock/filters/marketplace/Madeira.vue'
  import ReprocessStockMarketplaceNetshoesModule from '@/components/tool/batch/reprocessStock/filters/marketplace/Netshoes.vue'
  import ReprocessStockMarketplaceSkyhubModule from '@/components/tool/batch/reprocessStock/filters/marketplace/Skyhub.vue'
  import ReprocessStockMarketplaceWalmartModule from '@/components/tool/batch/reprocessStock/filters/marketplace/Walmart.vue'
  import ReprocessStockMarketplaceWebcontinentalModule from '@/components/tool/batch/reprocessStock/filters/marketplace/Webcontinental.vue'
  import ReprocessStockMarketplaceZoomModule from '@/components/tool/batch/reprocessStock/filters/marketplace/Zoom.vue'
  import ReprocessStockMarketplaceCentauroModule from '@/components/tool/batch/reprocessStock/filters/marketplace/Centauro.vue'
  import ReprocessStockMarketplaceRennerModule from '@/components/tool/batch/reprocessStock/filters/marketplace/Renner.vue'
  import ReprocessStockMarketplaceMercadoLivreModule from '@/components/tool/batch/reprocessStock/filters/marketplace/MercadoLivre.vue'
  import ReprocessStockMarketplaceSheinModule from '@/components/tool/batch/reprocessStock/filters/marketplace/Shein.vue'
  import ReprocessStockMarketplaceTikTokShopModule from '@/components/tool/batch/reprocessStock/filters/marketplace/TikTokShop.vue'

  import SharedMarketplacePriceStatus from '@/components/tool/batch/shared/filters/marketplace/PriceStatus.vue'
  import SharedMarketplaceProductStatus from '@/components/tool/batch/shared/filters/marketplace/ProductStatus.vue'
  import SharedMarketplaceStockStatus from '@/components/tool/batch/shared/filters/marketplace/StockStatus.vue'

  import UpdatePriceEcommerceMoovinSource from '@/components/tool/batch/updatePrice/filters/ecommerce/Moovin.vue'
  import UpdatePriceErpTinySource from '@/components/tool/batch/updatePrice/filters/erp/Tiny.vue'
  import UpdatePriceMarketplaceCnovaModule from '@/components/tool/batch/updatePrice/filters/marketplace/Cnova.vue'
  import UpdatePriceMarketplaceColomboModule from '@/components/tool/batch/updatePrice/filters/marketplace/Colombo.vue'
  import UpdatePriceMarketplaceMagaluModule from '@/components/tool/batch/updatePrice/filters/marketplace/Magalu.vue'
  import UpdatePriceMarketplaceMadeiraModule from '@/components/tool/batch/updatePrice/filters/marketplace/Madeira.vue'
  import UpdatePriceMarketplaceMercadoLivreModule from '@/components/tool/batch/updatePrice/filters/marketplace/MercadoLivre.vue'
  import UpdatePriceMarketplaceNetshoesModule from '@/components/tool/batch/updatePrice/filters/marketplace/Netshoes.vue'
  import UpdatePriceMarketplaceSkyhubModule from '@/components/tool/batch/updatePrice/filters/marketplace/Skyhub.vue'
  import UpdatePriceMarketplaceWalmartModule from '@/components/tool/batch/updatePrice/filters/marketplace/Walmart.vue'
  import UpdatePriceMarketplaceWebcontinentalModule from '@/components/tool/batch/updatePrice/filters/marketplace/Webcontinental.vue'
  import UpdatePriceMarketplaceZoomModule from '@/components/tool/batch/updatePrice/filters/marketplace/Zoom.vue'
  import UpdatePriceMarketplaceCentauroModule from '@/components/tool/batch/updatePrice/filters/marketplace/Centauro.vue'
  import UpdatePriceToolApiSource from '@/components/tool/batch/updatePrice/filters/tool/Api.vue'
  import UpdatePriceMarketplaceShopeeModule from '@/components/tool/batch/updatePrice/filters/marketplace/Shopee.vue'

  import UpdateReducerEcommerceMoovinSource from '@/components/tool/batch/updateReducer/filters/ecommerce/Moovin.vue'
  import UpdateReducerErpTinySource from '@/components/tool/batch/updateReducer/filters/erp/Tiny.vue'
  import UpdateReducerMarketplaceCnovaModule from '@/components/tool/batch/updateReducer/filters/marketplace/Cnova.vue'
  import UpdateReducerMarketplaceColomboModule from '@/components/tool/batch/updateReducer/filters/marketplace/Colombo.vue'
  import UpdateReducerMarketplaceMagaluModule from '@/components/tool/batch/updateReducer/filters/marketplace/Magalu.vue'
  import UpdateReducerMarketplaceMadeiraModule from '@/components/tool/batch/updateReducer/filters/marketplace/Madeira.vue'
  import UpdateReducerMarketplaceMercadoLivreModule from '@/components/tool/batch/updateReducer/filters/marketplace/MercadoLivre.vue'
  import UpdateReducerMarketplaceNetshoesModule from '@/components/tool/batch/updateReducer/filters/marketplace/Netshoes.vue'
  import UpdateReducerMarketplaceSkyhubModule from '@/components/tool/batch/updateReducer/filters/marketplace/Skyhub.vue'
  import UpdateReducerMarketplaceWalmartModule from '@/components/tool/batch/updateReducer/filters/marketplace/Walmart.vue'
  import UpdateReducerMarketplaceWebcontinentalModule from '@/components/tool/batch/updateReducer/filters/marketplace/Webcontinental.vue'
  import UpdateReducerMarketplaceZoomModule from '@/components/tool/batch/updateReducer/filters/marketplace/Zoom.vue'
  import UpdateReducerMarketplaceCentauroModule from '@/components/tool/batch/updateReducer/filters/marketplace/Centauro.vue'
  import UpdateReducerToolApiSource from '@/components/tool/batch/updateReducer/filters/tool/Api.vue'
  import UpdateReducerMarketplaceShopeeModule from '@/components/tool/batch/updateReducer/filters/marketplace/Shopee.vue'
  import UpdateReducerMarketplaceSheinModule from '@/components/tool/batch/updateReducer/filters/marketplace/Shein.vue'
  import UpdateReducerMarketplaceTikTokShopModule from '@/components/tool/batch/updateReducer/filters/marketplace/TikTokShop.vue'

  import InactivateProductEcommerceMoovinSource from '@/components/tool/batch/inactivateProduct/filters/ecommerce/Moovin.vue'
  import InactivateProductOptions from '@/components/tool/batch/inactivateProduct/Options.vue'
  import InactivateProductMarketplaceRennerModule from '@/components/tool/batch/inactivateProduct/filters/marketplace/Renner.vue'

  import ChangeAnnouncementStatusMarketplaceMercadoLivreModule from '@/components/tool/batch/changeAnnouncementStatus/filters/marketplace/MercadoLivre.vue'
  import ChangeAnnouncementStatusOptions from '@/components/tool/batch/changeAnnouncementStatus/Options.vue'

  import ChangeAnnouncementStatusEcommerceMoovinSource from '@/components/tool/batch/changeAnnouncementStatus/filters/ecommerce/Moovin.vue'
  import ChangeAnnouncementStatusErpTinySource from '@/components/tool/batch/changeAnnouncementStatus/filters/erp/Tiny.vue'
  import ChangeAnnouncementStatusToolApiSource from '@/components/tool/batch/changeAnnouncementStatus/filters/tool/Api.vue'

  import ReprocessPriceMarketplaceRennerModule from '@/components/tool/batch/reprocessPrice/filters/marketplace/Renner.vue'
  import ReprocessPriceMarketplaceSheinModule from '@/components/tool/batch/reprocessPrice/filters/marketplace/Shein.vue'
  import ReprocessPriceMarketplaceTikTokShopModule from '@/components/tool/batch/reprocessPrice/filters/marketplace/TikTokShop.vue'

  import RemoveProductMarketplaceNetshoesModule from '@/components/tool/batch/removeProduct/filters/marketplace/Netshoes.vue'
  import RemoveProductMarketplaceShopeeModule from '@/components/tool/batch/removeProduct/filters/marketplace/Shopee.vue'
  import RemoveProductMarketplaceAmazonModule from '@/components/tool/batch/removeProduct/filters/marketplace/Amazon.vue'
  import RemoveProductMarketplaceMagaluModule from '@/components/tool/batch/removeProduct/filters/marketplace/Magalu.vue'
  import RemoveProductMarketplaceRennerModule from '@/components/tool/batch/removeProduct/filters/marketplace/Renner.vue'
  import RemoveProductMarketplaceCnovaModule from '@/components/tool/batch/removeProduct/filters/marketplace/Cnova.vue'
  import RemoveProductMarketplaceRamarimModule from '@/components/tool/batch/removeProduct/filters/marketplace/Ramarim.vue'
  import RemoveProductMarketplaceCentauroModule from '@/components/tool/batch/removeProduct/filters/marketplace/Centauro.vue'
  import RemoveProductMarketplaceSicrediModule from '@/components/tool/batch/removeProduct/filters/marketplace/Sicredi.vue'
  import RemoveProductMarketplaceSicoobModule from '@/components/tool/batch/removeProduct/filters/marketplace/Sicoob.vue'
  import RemoveProductMarketplaceSheinModule from '@/components/tool/batch/removeProduct/filters/marketplace/Shein.vue'

  export default {
    data: () => ({
      loading: true,
      loadingCategories: false,
      moduleSelected: null,
      module: null,
      sourceSelected: null,
      sources: [],
      source: null,
      actionSelected: null,
      action: {},
      saving: false,
      comment: null,
      canNotSave: false,
      showModalConfirm: false
    }),
    mounted () {
      this.fetchModules()
        .catch(() => {})
        .then(() => {
          this.loading = false
        })
    },
    methods: {
      ...mapActions('errors', [
        'addError',
        'clearErrors'
      ]),
      ...mapActions('toolBatchTask', [
        'fetchModules',
        'fetchActions',
        'clearActions'
      ]),
      async showModal () {
        this.showModalConfirm = true
      },
      async save () {
        this.showModalConfirm = false
        this.clearErrors()

        if (!this.module) {
          this.addError({
            property: 'module',
            error: {
              message: 'required',
              value: 1
            }
          })
          notifier.error('Campo módulo obrigatório.', 'Erro')
          return false
        }

        if (!this.action) {
          this.addError({
            property: 'action',
            error: {
              message: 'required',
              value: 1
            }
          })
          notifier.error('Campo ação obrigatório.', 'Erro')
          return false
        }

        if (!this.source && this.action.hasProductSource && this.module.type === 'marketplace') {
          this.addError({
            property: 'source',
            error: {
              message: 'required',
              value: 1
            }
          })
          notifier.error('Campo módulo fonte obrigatório.', 'Erro')
          return false
        }

        let options = []
        if (this.$refs.actionOptionComponent) {
          options = this.$refs.actionOptionComponent.getOptions()

          if (options.hasOwnProperty('file') && options.file === '') {
            this.addError({
              property: 'file',
              error: {
                message: 'required',
                value: 1
              }
            })
            notifier.error('Campo de arquivo obrigatório.', 'Erro')
            return false
          }
        }

        this.saving = true

        let moduleFilters = []
        if (this.$refs.actionModuleFilterComponent) {
          moduleFilters = this.$refs.actionModuleFilterComponent.filters
        }

        if (this.$refs.productCodesFilterComponent) {
          const { productIds, skus } = this.$refs.productCodesFilterComponent.filters
          moduleFilters = Array.isArray(moduleFilters) ? {} : moduleFilters
          if (productIds && productIds.trim()) {
            moduleFilters = merge(moduleFilters, { productIds: productIds.trim() })
          }
          if (skus && skus.trim()) {
            moduleFilters = merge(moduleFilters, { skus: skus.trim() })
          }
        }

        let source = null
        if (this.source) {
          moduleFilters = merge(moduleFilters, { source: this.source })
        }

        if (this.$refs.actionSourceFilterComponent) {
          source = merge(this.source, this.$refs.actionSourceFilterComponent.filters)
          moduleFilters = merge(moduleFilters, { source: source })
        }

        const task = {
          'identifier': this.action.identifier,
          'comment': this.comment,
          'module': this.module,
          'options': options,
          'filters': moduleFilters,
          'totalItems': 0
        }

        try {
          await api.saveTask(task)

          notifier.success('Tarefa cadastrada com sucesso.', 'Sucesso')

          this.$router.push('/tool/batch/tasks')
        } catch (error) {
          notifier.error('Erro ao cadastrar a tarefa.', 'Erro')
        }

        this.saving = false
      },
      clearModule () {
        this.module = null
      },
      clearSource () {
        this.sources = []
        this.source = null
        this.sourceSelected = null
      },
      clearAction () {
        this.clearActions()
        this.action = null
        this.actionSelected = null
      }
    },
    computed: {
      ...mapFields({
        toolBatchTaskActions: 'toolBatchTask.actions',
        toolBatchTaskModules: 'toolBatchTask.modules'
      }),
      ...mapGetters('errors', [
        'getError'
      ]),
      actionOptionComponent () {
        if (!this.actionSelected) {
          return false
        }

        return `${upperFirst(this.actionSelected)}Options`
      },
      showProductCodesFilter () {
        // Mercado Livre trabalha por anúncio e ainda não suporta esse filtro
        return !!(this.actionSelected && this.action.hasProductCodesFilter &&
          this.module && this.module.type === 'marketplace' && this.module.identifier !== 'mercadoLivre')
      },
      actionModuleFilterComponent () {
        if (!this.actionSelected || !this.module) {
          return false
        }

        if (this.actionSelected === 'reportOrderStatus') {
          return `${upperFirst(this.actionSelected)}${upperFirst(this.module.type)}SharedModule`
        }

        return `${upperFirst(this.actionSelected)}${upperFirst(this.module.type)}${upperFirst(this.module.identifier)}Module`
      },
      actionSourceFilterComponent () {
        if (!this.actionSelected || !this.source) {
          return false
        }

        return `${upperFirst(this.actionSelected)}${upperFirst(this.source.type)}${upperFirst(this.source.identifier)}Source`
      }
    },
    watch: {
      async moduleSelected (value) {
        this.clearModule()
        this.clearAction()
        this.clearSource()

        if (value === null) {
          return false
        }

        const [type, identifier] = value.split('.')
        this.module = {
          type: type,
          identifier: identifier
        }

        await this.fetchActions(this.module)
      },
      actionSelected (value) {
        this.clearSource()
        this.clearErrors()

        if (value === null) {
          this.action = {}

          return false
        }

        this.action = this.toolBatchTaskActions.find(action => action.identifier === value)

        if (this.action.hasProductSource && this.module.type === 'marketplace') {
          this.loadingCategories = true

          const promises = []

          for (let i in this.action.sources) {
            const source = this.action.sources[i]

            promises.push(
              api.sourceAvailableForProduct(this.module, source)
                .then(() => this.sources.push(source))
                .catch(() => {})
            )
          }

          Promise.all(promises)
            .then(() => {
              if (!this.sources.length) {
                notifier.error(`Nenhuma fonte possui produto disponibilizados no "${this.$t(`${this.module.type}.label`)} ${this.$t(`${this.module.identifier}.label`)}".`, 'Erro')
                this.moduleSelected = null
              }
            })
            .finally(() => { this.loadingCategories = false })
        } else {
          this.sources = this.action.sources
        }
      },
      async sourceSelected (value) {
        if (value === null) {
          this.source = null

          return false
        }

        const [type, identifier] = value.split('.')
        this.source = {
          type: type,
          identifier: identifier
        }
      }
    },
    components: {
      ProductCodes,
      ModalConfirm,
      PageTitle,
      Panel,
      Loader,
      FormGroup,
      // ExportProductCsvEcommerceMoovinSource,
      ExportProductCsvErpTinySource,
      ExportProductCsvToolApiSource,
      ExportProductCsvMarketplaceCarrefourModule,
      ExportProductCsvMarketplaceCnovaModule,
      ExportProductCsvMarketplaceColomboModule,
      ExportProductCsvMarketplaceGfgModule,
      ExportProductCsvMarketplaceMagaluModule,
      ExportProductCsvMarketplaceLeroyModule,
      ExportProductCsvMarketplaceAmazonModule,
      ExportProductCsvMarketplaceMadeiraModule,
      ExportProductCsvMarketplaceMercadoLivreModule,
      ExportProductCsvMarketplaceNetshoesModule,
      ExportProductCsvMarketplaceSkyhubModule,
      ExportProductCsvMarketplaceSicoobModule,
      ExportProductCsvMarketplaceSicrediModule,
      ExportProductCsvMarketplaceMaisCorreiosModule,
      ExportProductCsvMarketplaceWalmartModule,
      ExportProductCsvMarketplaceWebcontinentalModule,
      ExportProductCsvMarketplacePolishopModule,
      ExportProductCsvMarketplaceZoomModule,
      ExportProductCsvMarketplaceCentauroModule,
      ExportProductCsvToolApiModule,
      ExportProductCsvMarketplaceShopeeModule,
      ExportProductCsvMarketplaceSheinModule,
      ExportProductCsvMarketplaceTikTokShopModule,
      ExportOrderCsvMarketplaceSkyhubModule,
      ExportOrderCsvMarketplaceCarrefourModule,
      ExportOrderCsvMarketplaceCnovaModule,
      ExportOrderCsvMarketplaceColomboModule,
      ExportOrderCsvMarketplaceCentauroModule,
      ExportOrderCsvMarketplaceMagaluModule,
      ExportOrderCsvMarketplaceLeroyModule,
      ExportOrderCsvMarketplaceAmazonModule,
      ExportOrderCsvMarketplaceSicoobModule,
      ExportOrderCsvMarketplaceSicrediModule,
      ExportOrderCsvMarketplaceMaisCorreiosModule,
      ExportOrderCsvMarketplaceMadeiraModule,
      ExportOrderCsvMarketplaceMercadoLivreModule,
      ExportOrderCsvMarketplaceNetshoesModule,
      ExportOrderCsvMarketplaceWebcontinentalModule,
      ExportOrderCsvMarketplaceZoomModule,
      ExportOrderCsvMarketplacePolishopModule,
      ExportOrderCsvMarketplaceShopeeModule,
      ExportOrderCsvMarketplaceSheinModule,
      ExportOrderCsvMarketplaceTikTokShopModule,
      ReportOrderStatusMarketplaceSharedModule,
      FeedUpdatePriceOptions,
      FeedUpdateReducerOptions,
      FileUpdatePriceOptions,
      FileReprocessOrderOptions,
      UpdatePriceOptions,
      UpdateReducerOptions,
      ExportProductCsvOptions,
      ProductReprocessMarketplaceAmazonModule,
      FeedUpdatePriceEcommerceMoovinSource,
      FeedUpdatePriceErpTinySource,
      FeedUpdatePriceMarketplaceCarrefourModule,
      FeedUpdatePriceMarketplaceGfgModule,
      FeedUpdatePriceMarketplaceLeroyModule,
      FeedUpdatePriceMarketplaceAmazonModule,
      FeedUpdatePriceToolApiSource,
      FeedUpdateReducerEcommerceMoovinSource,
      FeedUpdateReducerErpTinySource,
      FeedUpdateReducerMarketplaceCarrefourModule,
      FeedUpdateReducerMarketplaceGfgModule,
      FeedUpdateReducerMarketplaceLeroyModule,
      FeedUpdateReducerMarketplaceAmazonModule,
      FeedUpdateReducerToolApiSource,
      MigrationAnnouncementMarketplaceMercadoLivreModule,
      ReprocessFeedPriceMarketplaceCarrefourModule,
      ReprocessFeedPriceMarketplaceGfgModule,
      ReprocessFeedPriceMarketplaceLeroyModule,
      ReprocessFeedPriceMarketplaceAmazonModule,
      ReprocessFeedProductMarketplaceCarrefourModule,
      ReprocessFeedProductMarketplaceGfgModule,
      ReprocessFeedProductMarketplaceLeroyModule,
      ReprocessFeedProductMarketplaceAmazonModule,
      ReprocessFeedStockMarketplaceCarrefourModule,
      ReprocessFeedStockMarketplaceGfgModule,
      ReprocessProductMarketplacePrivaliaModule,
      ReprocessFeedStockMarketplaceLeroyModule,
      ReprocessFeedStockMarketplaceAmazonModule,
      ReprocessPriceMarketplaceCnovaModule,
      ReprocessPriceMarketplaceColomboModule,
      ReprocessPriceMarketplaceShopeeModule,
      ReprocessPriceMarketplaceMagaluModule,
      ReprocessPriceMarketplaceMadeiraModule,
      ReprocessPriceMarketplaceNetshoesModule,
      ReprocessPriceMarketplaceSkyhubModule,
      ReprocessPriceMarketplaceWalmartModule,
      ReprocessPriceMarketplaceWebcontinentalModule,
      ReprocessPriceMarketplaceZoomModule,
      ReprocessPriceMarketplaceCentauroModule,
      ReprocessPriceMarketplaceRennerModule,
      ReprocessPriceMarketplaceSheinModule,
      ReprocessPriceMarketplaceTikTokShopModule,
      ReprocessProductMarketplaceCnovaModule,
      ReprocessProductMarketplaceRennerModule,
      ReprocessProductMarketplaceShopeeModule,
      ReprocessProductMarketplaceColomboModule,
      ReprocessProductMarketplaceMagaluModule,
      ReprocessProductMarketplaceMadeiraModule,
      ReprocessProductMarketplaceNetshoesModule,
      ReprocessProductMarketplaceSkyhubModule,
      ReprocessProductMarketplaceWalmartModule,
      ReprocessProductMarketplaceWebcontinentalModule,
      ReprocessProductMarketplaceZoomModule,
      ReprocessProductMarketplaceCentauroModule,
      ReprocessProductMarketplaceSicoobModule,
      ReprocessProductMarketplaceSicrediModule,
      ReprocessProductMarketplaceMaisCorreiosModule,
      ReprocessProductMarketplacePolishopModule,
      ReprocessProductMarketplaceSheinModule,
      ReprocessProductMarketplaceTikTokShopModule,
      ReprocessStockMarketplaceCnovaModule,
      ReprocessStockMarketplaceColomboModule,
      ReprocessStockMarketplaceShopeeModule,
      ReprocessStockMarketplaceMagaluModule,
      ReprocessStockMarketplaceMadeiraModule,
      ReprocessStockMarketplaceNetshoesModule,
      ReprocessStockMarketplaceSkyhubModule,
      ReprocessStockMarketplaceWalmartModule,
      ReprocessStockMarketplaceWebcontinentalModule,
      ReprocessStockMarketplaceZoomModule,
      ReprocessStockMarketplaceCentauroModule,
      ReprocessStockMarketplaceRennerModule,
      ReprocessStockMarketplaceMercadoLivreModule,
      ReprocessStockMarketplaceSheinModule,
      ReprocessStockMarketplaceTikTokShopModule,
      SharedMarketplacePriceStatus,
      SharedMarketplaceProductStatus,
      SharedMarketplaceStockStatus,
      UpdatePriceEcommerceMoovinSource,
      UpdatePriceErpTinySource,
      UpdatePriceMarketplaceCnovaModule,
      UpdatePriceMarketplaceColomboModule,
      UpdatePriceMarketplaceMagaluModule,
      UpdatePriceMarketplaceMadeiraModule,
      UpdatePriceMarketplaceMercadoLivreModule,
      UpdatePriceMarketplaceNetshoesModule,
      UpdatePriceMarketplaceSkyhubModule,
      UpdatePriceMarketplaceWalmartModule,
      UpdatePriceMarketplaceWebcontinentalModule,
      UpdatePriceMarketplaceZoomModule,
      UpdatePriceMarketplaceCentauroModule,
      UpdatePriceToolApiSource,
      UpdatePriceMarketplaceShopeeModule,
      UpdateReducerEcommerceMoovinSource,
      UpdateReducerErpTinySource,
      UpdateReducerMarketplaceCnovaModule,
      UpdateReducerMarketplaceColomboModule,
      UpdateReducerMarketplaceMagaluModule,
      UpdateReducerMarketplaceMadeiraModule,
      UpdateReducerMarketplaceMercadoLivreModule,
      UpdateReducerMarketplaceNetshoesModule,
      UpdateReducerMarketplaceSkyhubModule,
      UpdateReducerMarketplaceWalmartModule,
      UpdateReducerMarketplaceWebcontinentalModule,
      UpdateReducerMarketplaceZoomModule,
      UpdateReducerMarketplaceCentauroModule,
      UpdateReducerToolApiSource,
      UpdateReducerMarketplaceShopeeModule,
      UpdateReducerMarketplaceSheinModule,
      UpdateReducerMarketplaceTikTokShopModule,
      ChangeAnnouncementStatusMarketplaceMercadoLivreModule,
      ChangeAnnouncementStatusOptions,
      InactivateProductOptions,
      ChangeAnnouncementStatusEcommerceMoovinSource,
      ChangeAnnouncementStatusErpTinySource,
      InactivateProductEcommerceMoovinSource,
      ChangeAnnouncementStatusToolApiSource,
      RemoveProductMarketplaceNetshoesModule,
      InactivateProductMarketplaceRennerModule,
      RemoveProductMarketplaceAmazonModule,
      RemoveProductMarketplaceMagaluModule,
      RemoveProductMarketplaceShopeeModule,
      RemoveProductMarketplaceRennerModule,
      RemoveProductMarketplaceCnovaModule,
      RemoveProductMarketplaceRamarimModule,
      RemoveProductMarketplaceCentauroModule,
      RemoveProductMarketplaceSicrediModule,
      RemoveProductMarketplaceSicoobModule,
      RemoveProductMarketplaceSheinModule
    }
  }
</script>



// WEBPACK FOOTER //
// Create.vue?ab4c4936